import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { getProfile } from "@/api/profile";
import { useAuthStore } from "@/stores/auth";
import {
  Home,
  Calendar,
  Activity,
  TrendingUp,
  MessageCircle,
  Apple,
  Scale,
  Library,
  HeartPulse,
  Settings,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface NavItem {
  to: string;
  i18nKey: string;
  icon: LucideIcon;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/", i18nKey: "nav.today", icon: Home },
  { to: "/plan", i18nKey: "nav.plan", icon: Calendar },
  { to: "/workouts", i18nKey: "nav.workouts", icon: Activity },
  { to: "/reviews", i18nKey: "nav.reviews", icon: TrendingUp },
  { to: "/chat", i18nKey: "nav.coach", icon: MessageCircle },
  { to: "/nutrition", i18nKey: "nav.nutrition", icon: Apple },
  { to: "/body", i18nKey: "nav.body", icon: Scale },
  { to: "/knowledge", i18nKey: "nav.knowledge", icon: Library },
  { to: "/back", i18nKey: "nav.back_pain", icon: HeartPulse },
  { to: "/settings", i18nKey: "nav.settings", icon: Settings },
];

/**
 * Responsive single-source nav.
 *
 * md+ : sticky horizontal bar below TopNav (all 9 items, scrolls-x if cramped).
 * <md : floating bottom pill (same 9 items, horizontally scrollable).
 */
export function BottomNav() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const token = useAuthStore((s) => s.token);
  const profileQ = useQuery({
    queryKey: ["profile"],
    queryFn: getProfile,
    enabled: !!token,
    staleTime: 60_000,
  });
  const isAdmin = !!profileQ.data?.is_admin;
  const navItems = isAdmin
    ? [...NAV_ITEMS, { to: "/admin", i18nKey: "nav.admin", icon: ShieldCheck }]
    : NAV_ITEMS;
  const onAuthPage =
    pathname === "/login" ||
    pathname === "/register" ||
    pathname === "/onboarding" ||
    pathname === "/setup" || // back-compat — App.tsx redirects к /onboarding
    pathname === "/forgot-password" ||
    pathname === "/reset-password";
  if (onAuthPage) return null;

  return (
    <>
      {/* Desktop / tablet: sticky top bar — wide enough at 1280px for the
          full 10-item set + admin without horizontal scroll. */}
      <nav className="sticky top-0 z-30 hidden border-b border-cream-border/70 bg-cream/95 backdrop-blur md:block">
        <div className="mx-auto w-full max-w-7xl px-3 py-3 lg:px-6">
          <div className="flex items-center justify-center gap-0.5 overflow-x-auto lg:gap-1">
            {navItems.map((item) => (
              <DesktopItem
                key={item.to}
                item={item}
                label={t(item.i18nKey)}
                active={
                  pathname === item.to ||
                  (item.to !== "/" && pathname.startsWith(item.to + "/"))
                }
              />
            ))}
          </div>
        </div>
      </nav>

      {/* Mobile: floating bottom pill, horizontal scroll */}
      <nav
        aria-label={t("nav.aria_main")}
        className="fixed inset-x-3 bottom-3 z-40 rounded-2xl border border-cream-border bg-cream-light/95 shadow-soft backdrop-blur md:hidden"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <ul className="flex items-stretch gap-0.5 overflow-x-auto px-2 py-2">
          {navItems.map((item) => (
            <li key={item.to} className="shrink-0">
              <MobileItem
                item={item}
                label={t(item.i18nKey)}
                active={pathname === item.to}
              />
            </li>
          ))}
        </ul>
      </nav>
    </>
  );
}

function DesktopItem({
  item,
  label,
  active,
}: {
  item: NavItem;
  label: string;
  active: boolean;
}) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-2 text-[13px] transition-colors lg:gap-2 lg:px-3 lg:text-[14px]",
        active
          ? "bg-amber/15 font-medium text-amber-dark"
          : "text-navy-deep hover:bg-cream-border/40",
      )}
    >
      <Icon className="h-[18px] w-[18px]" strokeWidth={1.8} />
      <span>{label}</span>
    </NavLink>
  );
}

function MobileItem({
  item,
  label,
  active,
}: {
  item: NavItem;
  label: string;
  active: boolean;
}) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end
      className={cn(
        "flex min-w-[64px] flex-col items-center gap-0.5 rounded-xl px-2 py-1.5 text-center transition-colors",
        active ? "text-amber-dark" : "text-taupe hover:text-navy-deep",
      )}
    >
      <Icon className="h-5 w-5" strokeWidth={1.8} />
      <span className="text-[10px] leading-tight">{label}</span>
    </NavLink>
  );
}
